import { inject, Injectable } from '@angular/core';
import {
    AgentApiService,
    ConversationApiService,
    CustomerApiService,
} from '@dxp/core/api';
import {
    IConversationSummary,
    ICustomerRelations,
    IEmailMessageHistory,
    IHistoricActivities,
    IMessageHistory,
} from '@dxp/core/models';
import { saveAs } from 'file-saver';
import { map, Observable } from 'rxjs';
import { ChannelType, Direction, MediaGroup } from '@dxp/core/types';
import {
    mapConversationToActivity,
    mapWorkitemToActivity,
} from '../helpers/mappings';
import { ChatTranscriptService } from './chat-transcript.service';
import { EmailTranscriptService } from './email-transcript.service';

@Injectable({
    providedIn: 'root',
})
export class ActivityHistoryService {
    #conversationApiService = inject(ConversationApiService);
    #agentApiService = inject(AgentApiService);
    #customerApiService = inject(CustomerApiService);
    #chatTransciptService = inject(ChatTranscriptService);
    #emailTranscriptService = inject(EmailTranscriptService);

    fetchCustomerActivity(
        customerId: string,
        top: number,
        skip: number,
        filters: {
            agents?: string[];
            channels?: number[];
            channelTypes?: ChannelType[];
            direction?: Direction;
            completionCodes?: string[];
            date?: { from: Date | null; to: Date | null };
        },
        sort: 'oldest' | 'newest',
    ): Observable<IHistoricActivities> {
        return this.#conversationApiService
            .getConversationHistoryByCustomer(
                customerId,
                true,
                [
                    'workItems($expand=agent,queue)',
                    'queueSegments',
                    'rejectedEnqueueAttempts',
                    'channel',
                ],
                skip,
                top,
                filters,
                sort === 'newest' ? 'startedAtUtc desc' : 'startedAtUtc asc',
            )
            .pipe(
                map(historicConversations => {
                    return {
                        count: historicConversations.count,
                        historicActivity:
                            historicConversations.historicalConversations.map(
                                historicConversation =>
                                    mapConversationToActivity(
                                        historicConversation,
                                    ),
                            ),
                    };
                }),
            );
    }

    fetchAgentActivity(
        agentId: string,
        top: number,
        skip: number,
        filters: {
            queues?: string[];
            channels?: number[];
            channelTypes?: ChannelType[];
            direction?: Direction;
            completionCodes?: string[];
            date?: { from: Date | null; to: Date | null };
        },
        sort: 'oldest' | 'newest',
    ): Observable<IHistoricActivities> {
        return this.#agentApiService
            .getWorkitemHistoryByAgent(
                agentId,
                true,
                ['agent', 'channel', 'queue', 'conversations'],
                skip,
                top,
                filters,
                sort === 'newest' ? 'startTimeUtc desc' : 'startTimeUtc asc',
            )
            .pipe(
                map(historicConversations => {
                    return {
                        count: historicConversations.count,
                        historicActivity:
                            historicConversations.historicalWorkitems.map(
                                historicConversation =>
                                    mapWorkitemToActivity(historicConversation),
                            ),
                    };
                }),
            );
    }

    fetchConversationChatHistory(
        conversationId: string,
    ): Observable<IMessageHistory[]> {
        return this.#conversationApiService.getConversationMessagingActivityHistory(
            conversationId,
        );
    }

    fetchConversationEmailHistory(
        conversationId: string,
    ): Observable<IEmailMessageHistory[]> {
        return this.#conversationApiService.getConversationEmailActivityHistory(
            conversationId,
        );
    }

    downloadChatTranscript(
        conversationId: string,
        messages: IMessageHistory[],
    ) {
        this.#chatTransciptService
            .createTranscript(conversationId, messages)
            .subscribe(result => {
                saveAs(
                    result.blob,
                    result.isZip ? 'transcript.zip' : 'transcript.txt',
                );
            });
    }

    downloadEmailTranscript(messages: IEmailMessageHistory[]) {
        this.#emailTranscriptService
            .createTranscript(messages)
            .subscribe(result => {
                saveAs(result, 'transcript.eml');
            });
    }

    fetchCustomerRelations(customerId: string): Observable<ICustomerRelations> {
        return this.#customerApiService.getRelations(customerId);
    }

    fetchAgentRelations(
        agentId: string,
        channelTypes: ChannelType[],
        from: Date,
        to: Date,
    ) {
        return this.#agentApiService.getRelations(
            agentId,
            channelTypes,
            from,
            to,
        );
    }

    fetchConversationSummary(
        conversationId: string,
        media: MediaGroup,
    ): Observable<IConversationSummary> {
        return this.#conversationApiService.getConversationSummary(
            conversationId,
            media,
        );
    }
}
